/**
 * NotificationContext - In-app notification system
 *
 * Provides UI notifications (snackbar) and backend notification management.
 * Backend notifications are fetched via REST API on mount.
 */
import React, { createContext, useContext, useState, useCallback } from "react";
import NotificationSnackbar from "../components/NotificationSnackbar";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "info", // 'success', 'error', 'uploading', 'processing', 'archiving', 'indexed'
    autoHideDuration: 6000,
  });

  const showNotification = useCallback((message, type = "info", autoHideDuration = 6000) => {
    setNotification({
      open: true,
      message,
      type,
      autoHideDuration,
    });
  }, []);

  // Lifecycle-specific notification helpers
  const showFileLifecycleNotification = {
    archiving: (fileName) => {
      showNotification(`Archiving previous version of '${fileName}'...`, "info", 2000);
    },
    processing: (_fileName) => {
      // Don't show processing notifications - too verbose
      return;
    },
    indexed: (fileName) => {
      showNotification(`'${fileName}' indexed successfully`, "success", 5000);
    },
    failed: (fileName, reason = "") => {
      const message = reason
        ? `Failed to process '${fileName}': ${reason}`
        : `Failed to process '${fileName}'`;
      showNotification(message, "error", 8000);
    },
    uploadStart: (fileName) => {
      showNotification(`Starting upload for '${fileName}'...`, "info", 2000);
    },
    uploadComplete: (count) => {
      const fileText = count === 1 ? "file" : "files";
      showNotification(`${count} ${fileText} uploaded successfully`, "success", 3000);
    },
    batchProcessing: (_count) => {
      // Don't show batch processing notifications - too verbose
      return;
    },
    batchComplete: (successCount, failCount) => {
      if (failCount === 0) {
        const fileText = successCount === 1 ? "file" : "files";
        showNotification(`${successCount} ${fileText} indexed successfully`, "success", 5000);
      } else if (successCount === 0) {
        const fileText = failCount === 1 ? "file" : "files";
        showNotification(`${failCount} ${fileText} failed to process`, "error", 8000);
      } else {
        showNotification(
          `Processing complete: ${successCount} successful, ${failCount} failed`,
          "info",
          6000
        );
      }
    },
  };

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // Backend notifications state
  const [backendNotifications, setBackendNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch initial notifications on mount (only if authenticated)
  const fetchNotifications = useCallback(async () => {
    try {
      // Check if user is authenticated before fetching
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      if (!user.token) {
        return;
      }

      const { api } = require("../services/api");
      const notifications = await api.getNotifications(50, false);
      if (Array.isArray(notifications)) {
        setBackendNotifications(notifications);
        setUnreadCount(notifications.filter((n) => !n.read).length);
      }
    } catch (error) {
      // Don't log 401 errors (expected when token expires)
      if (!error?.message?.includes("401") && !error?.message?.includes("expired")) {
        console.error("Failed to fetch initial notifications:", error);
      }
    }
  }, []);

  React.useEffect(() => {
    // Only fetch if user is logged in
    const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
    if (user.token) {
      fetchNotifications();
    }
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (id) => {
    try {
      const { api } = require("../services/api");
      await api.markNotificationRead(id);
      // Update local state optimistic
      setBackendNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification read:", error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const { api } = require("../services/api");
      await api.markAllNotificationsRead();
      // Update local state
      setBackendNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications read:", error);
    }
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showFileLifecycleNotification,
        hideNotification,
        backendNotifications,
        unreadCount,
        fetchNotifications, // Expose to allow manual refresh
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={hideNotification}
        autoHideDuration={notification.autoHideDuration}
      />
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};

export default NotificationContext;
